import { convertGeoJSONToProvinces, updateProvincesData, DIFFICULTY_CONFIG } from '@/data/provinces';

// 阿里云DataV地图数据API
const CHINA_MAP_API = 'https://geo.datav.aliyun.com/areas_v3/bound/geojson?code=100000';
const CHINA_PROVINCES_API = 'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json';

// 本地数据路径（public目录下，可通过HTTP访问）
const LOCAL_CHINA_MAP_PATH = '/data/100000.json';
const LOCAL_CHINA_PROVINCES_PATH = '/data/100000_full.json';

// 缓存地图数据
let cachedMapData: any = null;
let cachedProvincesData: any = null;
let cachedFullMapData: any = null;

// 加载状态标记
let isLoadingMapData = false;
let isLoadingProvincesData = false;

/**
 * 从本地文件或远程API获取数据（本地优先）
 */
const fetchDataWithLocalFirst = async (localPath: string, apiUrl: string, dataName: string): Promise<any> => {
  // 首先尝试本地文件
  try {
    console.log(`📁 正在从本地获取${dataName}...`);
    const response = await fetch(localPath);
    
    if (!response.ok) {
      throw new Error(`Failed to load local data: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (!data) {
      throw new Error('Invalid local data format');
    }
    
    console.log(`✅ 本地${dataName}获取成功`);
    return data;
  } catch (error) {
    console.warn(`⚠️ 本地${dataName}获取失败，尝试远程API:`, error);
    
    // 本地文件失败，尝试远程API
    try {
      console.log(`🌐 正在从远程API获取${dataName}...`);
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data) {
        throw new Error('Invalid remote data format');
      }
      
      console.log(`✅ 远程API获取${dataName}成功`);
      return data;
    } catch (remoteError) {
      console.error(`❌ 远程API获取${dataName}也失败:`, remoteError);
      throw new Error(`无法获取${dataName}数据：本地文件和远程API都失败`);
    }
  }
};

/**
 * 获取中国地图GeoJSON数据（国界）- 本地优先
 */
export const fetchChinaMapData = async (): Promise<any> => {
  if (cachedMapData) {
    console.log('使用缓存的地图数据');
    return cachedMapData;
  }

  if (isLoadingMapData) {
    console.log('地图数据正在加载中，等待完成...');
    // 等待加载完成
    while (isLoadingMapData) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return cachedMapData;
  }

  try {
    isLoadingMapData = true;
    
    const mapData = await fetchDataWithLocalFirst(
      LOCAL_CHINA_MAP_PATH,
      CHINA_MAP_API, 
      '中国地图数据'
    );
    
    if (!mapData.features) {
      throw new Error('Invalid map data format: missing features');
    }
    
    console.log(`📍 地图数据包含 ${mapData.features.length} 个区域`);
    cachedMapData = mapData;
    
    return mapData;
  } catch (error) {
    console.error('获取地图数据失败:', error);
    throw error;
  } finally {
    isLoadingMapData = false;
  }
};

/**
 * 获取中国省级详细数据 - 本地优先
 */
export const fetchChinaProvincesData = async (): Promise<any> => {
  if (cachedFullMapData) {
    console.log('使用缓存的省级数据');
    return cachedFullMapData;
  }

  if (isLoadingProvincesData) {
    console.log('省级数据正在加载中，等待完成...');
    // 等待加载完成
    while (isLoadingProvincesData) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    return cachedFullMapData;
  }

  try {
    isLoadingProvincesData = true;
    
    const provincesData = await fetchDataWithLocalFirst(
      LOCAL_CHINA_PROVINCES_PATH,
      CHINA_PROVINCES_API, 
      '中国省级数据'
    );
    
    if (!provincesData.features) {
      throw new Error('Invalid provinces data format: missing features');
    }
    
    console.log(`🗺️ 省级数据包含 ${provincesData.features.length} 个省份`);
    cachedFullMapData = provincesData;
    
    return provincesData;
  } catch (error) {
    console.error('❌ 获取省级数据失败:', error);
    throw error;
  } finally {
    isLoadingProvincesData = false;
  }
};

/**
 * 初始化省份数据
 */
export const initializeProvincesData = async () => {
  if (cachedProvincesData) {
    console.log('使用缓存的转换后省份数据');
    return cachedProvincesData;
  }

  try {
    const provincesData = await fetchChinaProvincesData();
    const convertedData = convertGeoJSONToProvinces(provincesData);
    
    if (convertedData.length === 0) {
      console.warn('转换后的省份数据为空，使用默认数据');
      return null;
    }
    
    // 更新全局省份数据
    updateProvincesData(convertedData);
    
    // 更新困难模式的省份列表
    DIFFICULTY_CONFIG.hard.provinces = convertedData.map(p => p.id);
    
    console.log(`成功初始化 ${convertedData.length} 个省份数据`);
    cachedProvincesData = convertedData;
    
    return convertedData;
  } catch (error) {
    console.error('初始化省份数据失败:', error);
    // 返回默认数据作为备用
    return null;
  }
};

/**
 * 获取省份的地理中心点
 */
export const getProvinceCenter = (feature: any): [number, number] => {
  if (!feature || !feature.geometry || !feature.geometry.coordinates) {
    return [104, 35]; // 中国地理中心大概位置
  }
  
  // 处理不同的几何类型
  let coordinates: number[][];
  
  if (feature.geometry.type === 'Polygon') {
    coordinates = feature.geometry.coordinates[0];
  } else if (feature.geometry.type === 'MultiPolygon') {
    // 对于多多边形，取第一个多边形的第一个环
    coordinates = feature.geometry.coordinates[0][0];
  } else {
    return [104, 35];
  }
  
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return [104, 35];
  }
  
  // 计算边界框中心点
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  coordinates.forEach((coord: number[]) => {
    const [x, y] = coord;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });
  
  return [(minX + maxX) / 2, (minY + maxY) / 2];
};

/**
 * 检查点是否在多边形内（射线法）
 */
export const isPointInPolygon = (point: [number, number], polygon: number[][]): boolean => {
  const [x, y] = point;
  let inside = false;
  
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  
  return inside;
};

/**
 * 检查点是否在复杂几何体内（支持MultiPolygon）
 */
export const isPointInGeometry = (point: [number, number], geometry: any): boolean => {
  if (!geometry || !geometry.coordinates) return false;
  
  if (geometry.type === 'Polygon') {
    return isPointInPolygon(point, geometry.coordinates[0]);
  } else if (geometry.type === 'MultiPolygon') {
    // 检查是否在任何一个多边形内
    for (const polygon of geometry.coordinates) {
      if (isPointInPolygon(point, polygon[0])) {
        return true;
      }
    }
    return false;
  }
  
  return false;
};

/**
 * 计算两点之间的距离
 */
export const calculateDistance = (point1: [number, number], point2: [number, number]): number => {
  const [x1, y1] = point1;
  const [x2, y2] = point2;
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

/**
 * 获取省份的边界框
 */
export const getProvinceBounds = (feature: any): { minX: number; maxX: number; minY: number; maxY: number } => {
  if (!feature || !feature.geometry || !feature.geometry.coordinates) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  
  let coordinates: number[][];
  
  if (feature.geometry.type === 'Polygon') {
    coordinates = feature.geometry.coordinates[0];
  } else if (feature.geometry.type === 'MultiPolygon') {
    coordinates = feature.geometry.coordinates[0][0];
  } else {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  }
  
  let minX = Infinity, maxX = -Infinity;
  let minY = Infinity, maxY = -Infinity;
  
  coordinates.forEach((coord: number[]) => {
    const [x, y] = coord;
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minY = Math.min(minY, y);
    maxY = Math.max(maxY, y);
  });
  
  return { minX, maxX, minY, maxY };
};

/**
 * 简化几何坐标（减少点数以提高性能）
 */
export const simplifyGeometry = (geometry: any): any => {
  if (!geometry || !geometry.coordinates) return geometry;
  
  const simplifyPolygon = (coordinates: number[][]): number[][] => {
    if (coordinates.length <= 3) return coordinates;
    
    // 简单的抽稀算法：每隔几个点取一个
    const step = Math.max(1, Math.floor(coordinates.length / 50)); // 最多保留50个点
    const simplified = [];
    
    for (let i = 0; i < coordinates.length; i += step) {
      simplified.push(coordinates[i]);
    }
    
    // 确保闭合
    if (simplified[simplified.length - 1] !== simplified[0]) {
      simplified.push(simplified[0]);
    }
    
    return simplified;
  };
  
  if (geometry.type === 'Polygon') {
    return {
      ...geometry,
      coordinates: [simplifyPolygon(geometry.coordinates[0])]
    };
  } else if (geometry.type === 'MultiPolygon') {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map((polygon: number[][][]) => [
        simplifyPolygon(polygon[0])
      ])
    };
  }
  
  return geometry;
};

/**
 * 预加载地图数据（在应用启动时调用）
 */
export const preloadMapData = async () => {
  try {
    console.log('🚀 开始预加载地图数据（本地优先）...');
    
    // 并行加载国界和省级数据
    await Promise.all([
      fetchChinaMapData(),
      initializeProvincesData()
    ]);
    console.log('✅ 地图数据预加载完成');
  } catch (error) {
    console.warn('⚠️ 地图数据预加载失败，将使用默认数据:', error);
  }
};

/**
 * 清除缓存（用于调试或重新加载）
 */
export const clearCache = () => {
  cachedMapData = null;
  cachedProvincesData = null;
  cachedFullMapData = null;
  isLoadingMapData = false;
  isLoadingProvincesData = false;
  console.log('地图数据缓存已清除');
};
